import { camelToSnakeKeys, mapToNumeric, formatDateUS, snakeToCamelKeys, formatDateToString } from '@convenia/helpers'
import { mapCustomFieldValues } from '@convenia/common-organisms/SystemFields/helpers'

import { mapFiles } from './files'
import { masker } from '../../helpers'
import { PERSONAL_DOCUMENTS_AREA } from './areas'

export const mapCustomDocument = ({ documentFiles, label, ...data }, params) => ({
  ...snakeToCamelKeys(data),
  ...(label ? { name: label } : {}),
  label,
  files: mapFiles(documentFiles, params)
})

export const documentMappers = {
  rg: ({ documentFiles: files, issuingState, ...data }, params) => ({
    ...data,
    ...(issuingState ? { issuingStateId: +issuingState.id } : {}),
    emissionDate: formatDateUS(data.emissionDate),
    files: mapFiles(files, params)
  }),

  cpf: ({ documentFiles: files, ...data }, params) => ({
    ...data,
    cpf: masker('###.###.###-##', data.cpf),
    files: mapFiles(files, params),
    preventRemove: true
  }),

  ctps: ({ documentFiles: files, pis, emissionDate, issuingState, ...data }, params) => ({
    ...data,
    ...(issuingState ? { issuingStateId: +issuingState.id } : {}),
    pis: masker('###.#####.##-#', pis),
    emissionDate: formatDateUS(emissionDate),
    files: mapFiles(files, params)
  }),

  electoral_card: ({ documentFiles: files, ...data }, params) => ({
    ...data,
    ...(data.city ? { cityId: +data.city.id } : {}),
    ...(data.state ? { stateId: +data.state.id } : {}),
    electoralSection: data?.section,
    files: mapFiles(files, params)
  }),

  driver_license: ({ documentFiles: files, emissionDate, validateDate, ...data }, params) => ({
    ...data,
    emissionDate: formatDateUS(emissionDate),
    validateDate: formatDateUS(validateDate),
    files: mapFiles(files, params)
  }),

  reservist: ({ documentFiles: files, ...data }, params) => ({
    ...data,
    category: data.series,
    files: mapFiles(files, params)
  }),

  dismissal: ({ files, ...data }, params) => ({
    ...data,
    files: mapFiles(files, params)
  }),

  other_document: ({ files, ...data }, params) => ({
    ...data,
    files: mapFiles(files, params)
  }),

  files: (files, params) => mapFiles(files, params)
}

export const mapDocuments = (data, params) => ({
  cpf: documentMappers.cpf(data.cpf || {}, params),
  ctps: documentMappers.ctps(data.ctps || {}, params),
  rg: documentMappers.rg(data.rg || {}, params),
  reservist: documentMappers.reservist(data.reservist || {}, params),
  driverLicense: documentMappers.driver_license(data.driverLicense || {}, params),
  electoralCard: documentMappers.electoral_card(data.electoralCard || {}, params),
  ...(data.dismissal?.id
    ? { dismissal: documentMappers.dismissal(data.dismissal || {}, params) }
    : {}
  ),
})

export const mapCPFInput = ({ schema, data }) => ({
  ...camelToSnakeKeys({
    cpf: mapToNumeric(data?.cpf),
    id: data?.id,
    attachmentId: data?.attachmentId,
  }),
  custom_fields: mapCustomFieldValues({
    sectionId: PERSONAL_DOCUMENTS_AREA.CPF,
    data,
    schema
  })
})

export const mapCTPSInput = ({ schema, data }) => ({
  ...camelToSnakeKeys({
    id: data?.id,
    emissionDate: formatDateUS(data?.emissionDate, 'YYYY-MM-DD'),
    number: data?.number,
    pis: mapToNumeric(data?.pis),
    serialNumber: data?.serialNumber,
    issuingStateId: data?.issuingStateId,
  }),
  custom_fields: mapCustomFieldValues({
    sectionId: PERSONAL_DOCUMENTS_AREA.CTPS,
    data,
    schema
  })
})

export const mapRGInput = ({ schema, data }) => ({
  ...camelToSnakeKeys({
    id: data?.id,
    number: data?.number,
    emissionDate: formatDateUS(data?.emissionDate, 'YYYY-MM-DD'),
    issuingAgency: data?.issuingAgency,
    issuingStateId: data?.issuingStateId,
  }),
  custom_fields: mapCustomFieldValues({
    sectionId: PERSONAL_DOCUMENTS_AREA.RG,
    data,
    schema
  })
})

export const mapReservistInput = ({ schema, data }) => ({
  ...camelToSnakeKeys({
    id: data?.id,
    reservist: data?.reservist || '',
    raNumber: data?.raNumber || '',
    series: data?.category || '',
  }),
  custom_fields: mapCustomFieldValues({
    sectionId: PERSONAL_DOCUMENTS_AREA.RESERVIST,
    data,
    schema
  })
})

export const mapDriverLicenseInput = ({ schema, data }) => ({
  ...camelToSnakeKeys({
    number: data?.number || '',
    emissionDate: formatDateUS(data?.emissionDate, 'YYYY-MM-DD'),
    validateDate: formatDateUS(data?.validateDate, 'YYYY-MM-DD'),
    category: data?.category || '',
  }),
  custom_fields: mapCustomFieldValues({
    sectionId: PERSONAL_DOCUMENTS_AREA.CNH,
    data,
    schema
  })
})

export const mapElectoralCardInput = ({ schema, data }) => ({
  ...camelToSnakeKeys({
    number: data?.number || '',
    electoralWard: data?.electoralWard || '',
    section: data?.electoralSection || '',
    stateId: Number(data.stateId) || null,
    cityId: Number(data.cityId) || null,
  }),
  custom_fields: mapCustomFieldValues({
    sectionId: PERSONAL_DOCUMENTS_AREA.ELECTORAL,
    data,
    schema
  })
})

export const mapCustomDocumentInput = ({ schema, data }) => ({
  custom_fields: mapCustomFieldValues({
    sectionId: data?.sectionId,
    data,
    schema
  })
})

export const mapDismissalInput = (payload = {}) => camelToSnakeKeys({
  dismissalDate: formatDateToString(payload?.dismissal_date),
  dismissalReason: payload?.dismissal_reason,
})
